import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Monitor,
  Users,
  ArrowLeftRight,
  ShoppingCart,
  Tag,
  BarChart3,
  LogOut,
  Menu,
  X,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import LanguageSelector from "@/components/LanguageSelector";

export const Layout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  const navItems = [
    { path: "/", label: t("nav.dashboard"), icon: LayoutDashboard },
    { path: "/equipment", label: t("nav.equipment"), icon: Monitor },
    { path: "/employees", label: t("nav.employees"), icon: Users },
    { path: "/loans", label: t("nav.loans"), icon: ArrowLeftRight },
    { path: "/sales", label: t("nav.sales"), icon: ShoppingCart },
    { path: "/categories", label: t("nav.categories"), icon: Tag },
    { path: "/reports", label: t("nav.reports"), icon: BarChart3 },
    ...(isAdmin ? [{ path: "/users", label: "Benutzer", icon: Shield }] : []),
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex" data-testid="main-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col sidebar" data-testid="sidebar">
        <div className="p-4 border-b border-slate-200 bg-white">
          <img 
            src="https://customer-assets.emergentagent.com/job_equip-manager-26/artifacts/1ttifcr7_rt.png" 
            alt="Kaufmanns Consulting" 
            className="h-12 object-contain"
          />
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === "/"}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? "active" : ""}`
              }
              data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-200 space-y-2">
          <div className="flex justify-center">
            <LanguageSelector />
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-600 hover:text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            {t("nav.logout")}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <img 
          src="https://customer-assets.emergentagent.com/job_equip-manager-26/artifacts/1ttifcr7_rt.png" 
          alt="Kaufmanns Consulting" 
          className="h-8 object-contain"
        />
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white pt-16">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
            <button
              className="sidebar-link w-full text-red-600 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
              {t("nav.logout")}
            </button>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-0 mt-14 md:mt-0 overflow-auto dot-grid-bg min-h-screen">
        <div className="content-area animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
